import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { CheckIn } from '../../types/glp1';
import { CHECK_IN_KEYS, BANNER_KEY } from './glp1Copy';
import { colors, fontSize, fontWeight } from '../../style/tokens';
import {
  Fields, Field, FieldInner, FieldLabel,
  ToggleGroup, ToggleBtn, FormActions, BtnPrimary, BtnSkip, HelpText,
} from './journeyFormStyles';

/**
 * The 60-second tolerability check-in (§4.12).
 *
 * Always entered in-app, in every configuration — it asks how the user feels
 * RIGHT NOW, so no partner can supply it. Per-session and ephemeral: it is
 * discarded after scoring unless the user opts into trend storage (§3).
 *
 * Each item is optional. Ruling B2: one or two answers still score, on the mean
 * of what was given; all three skipped makes the indicator UNAVAILABLE, never
 * zero.
 *
 * This is not symptom collection and must never read as it.
 */
const ScaleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const ScaleHint = styled.span`
  font-size: ${fontSize.xs};
  color: ${colors.muted};
  flex-shrink: 0;
`;

const SevereLabel = styled.div`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${colors.text};
  line-height: 1.4;
  margin-bottom: 10px;
`;

const SCALE = [1, 2, 3, 4, 5];

interface Props {
  onSave: (checkIn: CheckIn, severeSymptom: boolean) => void;
  onSkip: () => void;
}

const CheckInStep: React.FC<Props> = ({ onSave, onSkip }) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [gi, setGi] = useState<number | undefined>();
  const [hydration, setHydration] = useState<number | undefined>();
  const [energy, setEnergy] = useState<number | undefined>();
  const [severe, setSevere] = useState<boolean | undefined>();

  const items: [string, number | undefined, (v: number) => void][] = [
    [t(CHECK_IN_KEYS.gi), gi, setGi],
    [t(CHECK_IN_KEYS.hydration), hydration, setHydration],
    [t(CHECK_IN_KEYS.energy), energy, setEnergy],
  ];

  return (
    <>
      <HelpText>{t(CHECK_IN_KEYS.subtitle)}</HelpText>

      <Fields>
        {items.map(([label, value, set]) => (
          <Field key={label}>
            <FieldInner>
              <FieldLabel>{label}</FieldLabel>
              <ScaleRow>
                <ScaleHint>{t(CHECK_IN_KEYS.scaleLow)}</ScaleHint>
                <ToggleGroup role="group" aria-label={label}>
                  {SCALE.map(n => (
                    <ToggleBtn
                      key={n}
                      type="button"
                      active={value === n}
                      aria-pressed={value === n}
                      onClick={() => set(n)}
                    >
                      {n}
                    </ToggleBtn>
                  ))}
                </ToggleGroup>
                <ScaleHint>{t(CHECK_IN_KEYS.scaleHigh)}</ScaleHint>
              </ScaleRow>
            </FieldInner>
          </Field>
        ))}

        {/*
          Ruling B3 — optional, and if present it MUST be a single neutral
          question after the three items. Never enumerate symptoms, never
          collect free text: the indicator must not read as symptom collection.
        */}
        <Field>
          <FieldInner>
            <SevereLabel>{t(CHECK_IN_KEYS.severeQuestion)}</SevereLabel>
            <ToggleGroup role="group" aria-label={t(CHECK_IN_KEYS.severeQuestion)}>
              <ToggleBtn type="button" active={severe === true} onClick={() => setSevere(true)}>
                {t(CHECK_IN_KEYS.severeYes)}
              </ToggleBtn>
              <ToggleBtn type="button" active={severe === false} onClick={() => setSevere(false)}>
                {t(CHECK_IN_KEYS.severeNo)}
              </ToggleBtn>
            </ToggleGroup>
          </FieldInner>
        </Field>
      </Fields>

      <FormActions>
        <BtnPrimary
          type="button"
          onClick={() => onSave({ gi, hydration, energy }, severe === true)}
        >
          {t(CHECK_IN_KEYS.continue)}
        </BtnPrimary>
        <BtnSkip type="button" onClick={onSkip}>{t(CHECK_IN_KEYS.skip)}</BtnSkip>
      </FormActions>

      {/* §2 — required on any screen showing a Journey element. */}
      <HelpText>{t(BANNER_KEY)}</HelpText>
    </>
  );
};

export default CheckInStep;
